import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Note, CreateNoteData, UpdateNoteData } from '../types/notes';

const COLLECTION_NAME = 'notes';

export const notesService = {
  // Összes jegyzet lekérése a felhasználóhoz
  async getNotes(userId: string): Promise<Note[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('isArchived', '!=', true),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as Note[];
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw new Error('Nem sikerült betölteni a jegyzeteket');
    }
  },

  // Új jegyzet létrehozása
  async createNote(userId: string, noteData: CreateNoteData): Promise<Note> {
    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...noteData,
        userId,
        createdAt: now,
        updatedAt: now,
        isArchived: false,
        color: noteData.color || 'yellow',
        priority: noteData.priority || 'medium',
        tags: noteData.tags || []
      });

      return {
        id: docRef.id,
        ...noteData,
        userId,
        createdAt: now.toDate().toISOString(),
        updatedAt: now.toDate().toISOString(),
        isArchived: false,
        color: noteData.color || 'yellow',
        priority: noteData.priority || 'medium',
        tags: noteData.tags || []
      };
    } catch (error) {
      console.error('Error creating note:', error);
      throw new Error('Nem sikerült létrehozni a jegyzetet');
    }
  },

  // Jegyzet frissítése
  async updateNote(noteId: string, updates: UpdateNoteData): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      
      // Undefined értékeket deleteField()-re cseréljük
      const updateData: any = { updatedAt: Timestamp.now() };
      
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        if (value === undefined) {
          updateData[key] = deleteField();
        } else {
          updateData[key] = value;
        }
      });
      
      await updateDoc(noteRef, updateData);
    } catch (error) {
      console.error('Error updating note:', error);
      throw new Error('Nem sikerült frissíteni a jegyzetet');
    }
  },

  // Jegyzet törlése
  async deleteNote(noteId: string): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await deleteDoc(noteRef);
    } catch (error) {
      console.error('Error deleting note:', error);
      throw new Error('Nem sikerült törölni a jegyzetet');
    }
  },

  // Jegyzet archiválása
  async archiveNote(noteId: string): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        isArchived: true,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error archiving note:', error);
      throw new Error('Nem sikerült archiválni a jegyzetet');
    }
  },

  // Archivált jegyzetek lekérése
  async getArchivedNotes(userId: string): Promise<Note[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('isArchived', '==', true),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as Note[];
    } catch (error) {
      console.error('Error fetching archived notes:', error);
      throw new Error('Nem sikerült betölteni az archivált jegyzeteket');
    }
  }
};
